import { User } from './../../../shared/models/user/user';
import { LocalStorageKeys } from './../../../shared/models/constants/storage.keys';
import { Injectable } from '@angular/core';
import { firstValueFrom, lastValueFrom, Observable, tap } from 'rxjs';
import { LoginRequestDTO } from 'src/app/shared/models/dtos/requests/login-request-dto/login-request-dto';
import { LoginResponseDTO } from 'src/app/shared/models/dtos/responses/login-response-dto/login-response-dto';
import { timeSpan } from '../../../utils/date';
import { UserService } from '../../api/user/user.service';
import { ResetPasswordRequestDTO } from 'src/app/shared/models/dtos/requests/reset-password-request-dto/reset-password-request-dto';
import { ConfirmResetPasswordRequestDTO } from 'src/app/shared/models/dtos/requests/confirm-reset-password-request-dto/confirm-reset-password-request-dto';
import { NotAuthorizedException } from 'src/app/shared/errors/cognito/cognito-errors';
import { TooManyTriesException } from './authentication-errors';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { AuthenticatedUserService } from '../authenticated-user/authenticated-user.service';
import { RefreshSessionResponseDTO } from 'src/app/shared/models/dtos/responses/refresh-session-response-dto/refresh-session-response-dto';
import { TranslationService } from '../translation/translation.service';
import { getTranslocoLang } from 'src/app/utils/translocoLang';

type TemporarySession = {
  email: string;
  token: string;
};

@Injectable({
  providedIn: 'root',
})
/**
 * Authentication service to handle authentication related work
 */
export class AuthenticationService {
  private _passwordResetEmail?: string;
  private _temporarySession!: TemporarySession;
  errorMessages = {
    backEndError: 'backend-comm-error',
    userShouldNotBeHere: 'user-should-not-be-here',
    noRefreshTokenFound: 'no-refresh-token-found',
  };
  representative!: Representative;

  /**
   * @return {string|undefined}
   */
  get passwordResetEmail(): string | undefined {
    return this._passwordResetEmail;
  }

  /**
   * @param {string | undefined} value
   */
  set passwordResetEmail(value: string | undefined) {
    this._passwordResetEmail = value?.trim();
  }

  /**
   * Injects Backend Service so that we can log into LnR system
   */
  constructor(
    private userService: UserService,
    private authenticatedUserService: AuthenticatedUserService,
    private translationService: TranslationService
  ) {
    this.resetTemporarySession();
    translationService.translateObject(this.errorMessages);
  }

  /**
   * Logs user in
   * @param {string} email
   * @param {string} password
   * @param {string} recaptcha
   */
  async logIn(
    email: string,
    password: string,
    recaptcha: string
  ): Promise<User> {
    // TODO: this logic does not belong here, I believe
    // it will be a different response from backend
    // that will alert this application of too many failed attempts
    const storedLoginAttempts = this.getLoginAttempts();

    const tenSeconds = 10000;

    if (timeSpan(storedLoginAttempts.timestamp) > tenSeconds) {
      localStorage.removeItem(LocalStorageKeys.LOGIN_ATTEMPTS);
    }

    const loginRequestDTO = new LoginRequestDTO(email, password, recaptcha);
    const token$ = this.userService.getAuthenticationToken(loginRequestDTO);
    let loginResponse: LoginResponseDTO;

    try {
      loginResponse = await lastValueFrom(token$);
    } catch (e) {
      if (e instanceof Error) {
        this.handleError(e);
        throw e;
      } else {
        throw new Error(this.errorMessages.backEndError);
      }
    }

    const accessToken = loginResponse.accessToken;
    const user = User.fromDTO(loginResponse);

    let representative: Representative;

    if (accessToken && accessToken.length > 0) {
      this.authenticatedUserService.user = user;
    }

    try {
      representative = await lastValueFrom(
        this.userService.getRepresentativeInfo()
      );
      const translocoLang = getTranslocoLang(representative.language!);
      this.translationService.setActiveLang(translocoLang);
      this.authenticatedUserService.representative = representative;
      this.representative = representative;
    } catch (e) {
      if (e instanceof Error) {
        this.handleError(e);
        throw e;
      } else {
        throw new Error(this.errorMessages.backEndError);
      }
    }

    if (!user || !representative) {
      throw new Error(this.errorMessages.backEndError); // Invalid response from backend?
    }

    return user;
  }

  /**
   * Resets password of given email
   * @param {string} email
   * @param {string} recaptcha
   * @return {Observable<any>}
   */
  resetPassword(email: string, recaptcha: string): Observable<any> {
    const resetPasswordRequestDTO = new ResetPasswordRequestDTO(
      email,
      recaptcha
    );
    this.passwordResetEmail = email;
    return this.userService.resetPassword(resetPasswordRequestDTO);
  }

  /**
   * Confirms reset password request, creating new password
   * @param {string} newPassword
   * @param {string} code
   * @param {string} recaptcha
   */
  async confirmResetPassword(
    newPassword: string,
    code: string,
    recaptcha: string
  ) {
    const email = this.passwordResetEmail!;

    if (!email) {
      throw new Error(this.errorMessages.userShouldNotBeHere);
    }

    const confirmResetPasswordRequestDTO = new ConfirmResetPasswordRequestDTO(
      email,
      newPassword,
      code,
      recaptcha
    );
    const ok$ = this.userService.confirmResetPassword(
      confirmResetPasswordRequestDTO
    );

    await lastValueFrom(ok$);
  }

  /**
   * counts failed login attempts
   */
  private countFailedLoginAttempt() {
    // artificial flow for failed authentication attempt
    // it will be a different response from backend
    // that will alert this application of too many failed attempts
    // TODO: create error class after discussing with team
    const storedLoginAttempts = this.getLoginAttempts();
    const loginAttempts = storedLoginAttempts.loginAttempts
      ? parseInt(storedLoginAttempts.loginAttempts) + 1
      : 1;
    localStorage.setItem(
      LocalStorageKeys.LOGIN_ATTEMPTS,
      JSON.stringify({
        loginAttempts,
        timestamp: new Date().getTime(),
      })
    );
  }

  /**
   * Returns stored login attempts object
   * @return {any} a local store item
   */
  private getLoginAttempts(): any {
    return JSON.parse(
      localStorage.getItem(LocalStorageKeys.LOGIN_ATTEMPTS) || '{}'
    );
  }

  /**
   * @param {string} loginAttempts
   * @return {boolean}
   */
  private tooManyTries(loginAttempts?: string): boolean {
    return Boolean(loginAttempts && parseInt(loginAttempts) >= 3);
  }

  /**
   * Handles Errors
   * @param {Error} e
   */
  private handleError(e: Error) {
    if (e instanceof NotAuthorizedException) {
      this.countFailedLoginAttempt();
      const storedLoginAttempts = this.getLoginAttempts();
      if (this.tooManyTries(storedLoginAttempts.loginAttempts)) {
        throw new TooManyTriesException();
      }
    }
  }

  /**
   * User logout by cleaning user data local storage
   */
  async logout() {
    const refreshToken = this.getRefreshToken();
    try {
      if (refreshToken) {
        await firstValueFrom(this.userService.logout(refreshToken));
      }
    } finally {
      this.authenticatedUserService.removeUser();
    }
  }

  /**
   * Refreshes session using refresh token
   * @return {Observable<RefreshSessionResponseDTO>}
   */
  refreshSession(): Observable<RefreshSessionResponseDTO> {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      return this.userService.refreshSession(refreshToken).pipe(
        tap((refreshSessionResponse) => {
          this.authenticatedUserService.updateTokens(
            refreshSessionResponse.accessToken,
            refreshSessionResponse.idToken
          );
        })
      );
    } else {
      throw new Error(this.errorMessages.noRefreshTokenFound);
    }
  }

  /**
   * Retrieves refresh token if available
   * @return {string | undefined}
   */
  private getRefreshToken(): string | undefined {
    return this.authenticatedUserService.user?.tokens?.refreshToken;
  }

  /**
   * @param {string} password
   * @param {string} email
   * @param {string} newPassword
   * @return {Promise<void>}
   */
  async changeTemporaryPassword(
    password: string,
    email: string,
    newPassword: string
  ): Promise<void> {
    const loginResponse = await firstValueFrom(
      this.userService.changeTemporaryPassword({
        password,
        email,
        newPassword,
      })
    );

    const accessToken = loginResponse.accessToken;
    const user = User.fromDTO(loginResponse);

    if (accessToken && accessToken.length > 0) {
      this.authenticatedUserService.user = user;
    }

    const representative = await lastValueFrom(
      this.userService.getRepresentativeInfo()
    );
    const translocoLang = getTranslocoLang(representative.language!);
    this.translationService.setActiveLang(translocoLang);
    this.authenticatedUserService.representative = representative;

    this.resetTemporarySession();

    if (!user || !representative) {
      throw new Error(this.errorMessages.backEndError); // Invalid response from backend?
    }
  }

  /**
   * Resets temporary session
   */
  private resetTemporarySession() {
    this._temporarySession = {
      token: '',
      email: '',
    };
  }

  /**
   * gets temporary authentication token
   */
  get temporaryToken() {
    return this._temporarySession.token;
  }
}
